import { array, bool, object, ObjectSchema, string } from 'yup';
import messages from 'messages';
import { labelFormFieldConfig } from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/utils';

import {
  WebviewButtonLinkStepFormFields,
  WebviewButtonLinkStepFormFieldsLinkNames,
  WebviewButtonLinkStepFormFieldsNames,
} from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/webview-button-link/types';

import { AutoCompleteLabel, FormFieldProps } from 'types';
import { editorValidation, emptyEditorValue, maxTelegramButtonLength } from 'utils';

export const webviewButtonLinkStepFormSchema: ObjectSchema<WebviewButtonLinkStepFormFields> =
  object({
    [WebviewButtonLinkStepFormFieldsNames.NodeId]: string().required(),
    [WebviewButtonLinkStepFormFieldsNames.Name]: string().required('Name is required'),
    [WebviewButtonLinkStepFormFieldsNames.Input]: editorValidation,
    [WebviewButtonLinkStepFormFieldsNames.Link]: object({
      [WebviewButtonLinkStepFormFieldsLinkNames.Url]: string()
        .required('URL is required')
        .test('is-url', 'Invalid URL', (value) => {
          // Regular expression for URL validation (allowing URLs without http/https protocols)
          const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)(\?.*)?$/;
          return urlRegex.test(value);
        }),
      [WebviewButtonLinkStepFormFieldsLinkNames.Text]: string()
        .max(
          maxTelegramButtonLength,
          `Text should be less than ${maxTelegramButtonLength} characters`,
        )
        .required('Button text is required'),
      [WebviewButtonLinkStepFormFieldsLinkNames.IncludeProjectParams]: bool().optional(),
      [WebviewButtonLinkStepFormFieldsLinkNames.WaitingForComplete]: bool().optional(),
    }).required(),
    [WebviewButtonLinkStepFormFieldsNames.OptionFullWidth]: bool(),
    [WebviewButtonLinkStepFormFieldsNames.Labels]: array<AutoCompleteLabel[]>().optional(),
  });

export const defaultWebviewButtonLinkStepFormValues: WebviewButtonLinkStepFormFields = {
  [WebviewButtonLinkStepFormFieldsNames.Name]: 'Webview Button Link',
  [WebviewButtonLinkStepFormFieldsNames.Input]: emptyEditorValue,
  [WebviewButtonLinkStepFormFieldsNames.NodeId]: '',
  [WebviewButtonLinkStepFormFieldsNames.Link]: {
    [WebviewButtonLinkStepFormFieldsLinkNames.Url]: '',
    [WebviewButtonLinkStepFormFieldsLinkNames.Text]: '',
    [WebviewButtonLinkStepFormFieldsLinkNames.IncludeProjectParams]: false,
    [WebviewButtonLinkStepFormFieldsLinkNames.WaitingForComplete]: false,
  },
  [WebviewButtonLinkStepFormFieldsNames.OptionFullWidth]: false,
  [WebviewButtonLinkStepFormFieldsNames.Labels]: [],
};

export const webviewButtonLinkFormFieldsConfig: Record<
  WebviewButtonLinkStepFormFieldsNames,
  FormFieldProps
> = {
  [WebviewButtonLinkStepFormFieldsNames.Name]: {
    label: 'Name',
    placeholder: 'Name',
    name: WebviewButtonLinkStepFormFieldsNames.Name,
    autoComplete: 'off',
    type: 'text',
  },
  [WebviewButtonLinkStepFormFieldsNames.Input]: {
    placeholder: '* Message',
    name: WebviewButtonLinkStepFormFieldsNames.Input,
    type: 'text',
  },
  [WebviewButtonLinkStepFormFieldsNames.Link]: {
    label: 'Webview Button',
    placeholder: 'Configure webview button',
    name: WebviewButtonLinkStepFormFieldsNames.Link,
    autoComplete: 'off',
    type: 'text',
  },
  [WebviewButtonLinkStepFormFieldsNames.NodeId]: {
    name: WebviewButtonLinkStepFormFieldsNames.NodeId,
    type: 'hidden',
  },
  [WebviewButtonLinkStepFormFieldsNames.OptionFullWidth]: {
    label: messages.builder.optionFullWidth,
    name: WebviewButtonLinkStepFormFieldsNames.OptionFullWidth,
    type: 'checkbox',
  },
  [WebviewButtonLinkStepFormFieldsNames.Labels]: labelFormFieldConfig,
};

export const WebviewButtonLinkFormFieldsConfig: Record<
  WebviewButtonLinkStepFormFieldsLinkNames,
  FormFieldProps
> = {
  [WebviewButtonLinkStepFormFieldsLinkNames.Url]: {
    label: 'URL',
    placeholder: 'https://example.com',
    name: WebviewButtonLinkStepFormFieldsLinkNames.Url,
    autoComplete: 'off',
    type: 'text',
  },
  [WebviewButtonLinkStepFormFieldsLinkNames.Text]: {
    label: 'Button Text',
    placeholder: 'Open App',
    name: WebviewButtonLinkStepFormFieldsLinkNames.Text,
    autoComplete: 'off',
    type: 'text',
  },
  [WebviewButtonLinkStepFormFieldsLinkNames.IncludeProjectParams]: {
    label: 'Include Project Params',
    name: WebviewButtonLinkStepFormFieldsLinkNames.IncludeProjectParams,
    type: 'checkbox',
  },
  [WebviewButtonLinkStepFormFieldsLinkNames.WaitingForComplete]: {
    label: 'Waiting for Complete',
    name: WebviewButtonLinkStepFormFieldsLinkNames.WaitingForComplete,
    type: 'checkbox',
  },
};
